import { useNavigate } from '@tanstack/react-router';
import { Button, HStack, Text, VStack } from 'ui_2';
import { Route as product_sku_route } from './index';
import { Route as service_index_route } from '../index';
import { Route as index_route } from '@routes/index';
import { ArcadeTicketIcon } from '@components/ArcadeTicketIcon';
import { ProductAvailability } from '@components/store/ProductAvailability';
import { BiChevronLeft } from 'react-icons/bi';
import { ProductRedemptionInstructions } from '@components/store/ProductRedemptionInstructions';
import { ProductTermsAndConditions } from '@components/store/ProductTermsAndConditions';
import { useArcadeStoreState } from '@src/hooks/useArcadeStoreState';
import { useBreakpoints, useFeeQuery } from '@puzzlehq/frontend-utils';
import { FeeView } from '@src/components/FeeBannerView';
import { usePurchaseHelper } from '@src/hooks/usePurchaseHelper';
import {
  useJoinParts,
  useJoinPartsStatus,
  usePartJoinHelper,
} from '@src/hooks/usePartJoin';
import { useEffect, useMemo, useState } from 'react';
import { JoinStatusDialog } from '@src/components/join/JoinStatusDialog';
import { AvailabilityCheckbox } from '@src/components/purchase/AvailabilityCheckbox';
import { PurchaseButton } from '@src/components/purchase/PurchaseButton';
import { usePurchaseStore } from './-purchaseStore';
import { useShallow } from 'zustand/react/shallow';
import { ProductShowcaseSlideshow } from '@src/components/smallBusiness/ProductShowcaseSlideshow';
import { trpcReact } from '@src/data/trpc';
import {
  useDisplayFlag,
  usePendingPurchasesForSKU,
} from '@src/hooks/useArcadeStore';
import { ViewProductDetails } from '@src/components/store/ViewProductDetails';
import { ProductPage } from '@src/components/layout/ProductPage';

export const View = () => {
  const productSKU = product_sku_route.useLoaderData();
  const navigate = useNavigate({ from: product_sku_route.fullPath });

  const { account, isPro, isConnected, isPuzzleWalletWebview } =
    useArcadeStoreState();

  const [status, _close] = usePurchaseStore(
    useShallow((state) => [state.status, state.close])
  );

  const { partPurchaseRecords, functionId, programId, recordCount } =
    usePurchaseHelper({ sku: productSKU });

  const { recordsToJoin, recordCount: joinRecordCount } = usePartJoinHelper({
    price: productSKU?.price,
  });
  const {
    mutate: joinParts,
    isLoading: isLoadingJoinParts,
    data: joinPartsRequests,
    error: joinPartsError,
  } = useJoinParts({ recordsToJoin });

  const { joinStatus, eventStatuses } = useJoinPartsStatus({
    requests: joinPartsRequests,
    isLoadingJoinParts,
  });
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const openJoinModal = () => setJoinDialogOpen(true);

  useEffect(() => {
    if (joinStatus === 'Joining') {
      setJoinDialogOpen(true);
    }
  }, [joinStatus]);

  useEffect(() => {
    console.log({ recordsToJoin });
  }, [JSON.stringify(recordsToJoin)]);

  useEffect(() => {
    console.log({
      recordCount,
      functionId,
      programId,
      plaintexts: partPurchaseRecords?.map((r) => r.plaintext),
    });
  }, [
    recordCount,
    functionId,
    programId,
    JSON.stringify(partPurchaseRecords?.map((r) => r.plaintext)),
  ]);

  const { data: feeData } = useFeeQuery({
    programId,
    functionId,
  });
  const fee = feeData?.fee?.fee.total ?? 0.5;

  const [checked, setChecked] = useState<boolean>(false);

  const imgUrl = productSKU.bigImageURL ?? productSKU.service.bigLogoImageURL;
  const url = productSKU.url ?? productSKU.service.url;
  const availability =
    productSKU.availability ?? productSKU.service.availability;
  const redemptionInstructions =
    productSKU.redemptionInstructions ??
    productSKU.service.redemptionInstructions;
  const termsAndConditions =
    productSKU.termsAndConditions ?? productSKU.service.termsAndConditions;
  const stock = productSKU.stock;
  const inStock = stock !== undefined && stock > 0;

  const { isMedium } = useBreakpoints();

  const { hasPendingPurchases } = usePendingPurchasesForSKU(productSKU.skuId);

  const displayFlag = useDisplayFlag({ sku: productSKU });

  const trpcReactUtils = trpcReact.useUtils();
  useEffect(() => {
    if (status === 'succeeded' && account) {
      trpcReactUtils.getOrders.invalidate({
        address: account.address,
        network: import.meta.env.VITE_NETWORK!,
        status: 'pending',
      });
    }
  }, [status]);

  const previewImageURLs = useMemo(
    () => productSKU.service.previewImageURLs,
    [JSON.stringify(productSKU.service.previewImageURLs)]
  );

  return (
    <ProductPage
      productSKU={productSKU}
      backRoute={index_route.to}
      onBack={_close}
    >
      <div className='flex w-full flex-col gap-4'>
        <div className='flex h-full w-full flex-col md:flex-row md:gap-12 lg:gap-20'>
          {/* Service Image + Product info */}
          <VStack className='w-full gap-3 md:w-[55%] md:max-w-[600px]'>
            {previewImageURLs && previewImageURLs.length > 0 ? (
              <ProductShowcaseSlideshow images={previewImageURLs} isSkuPage />
            ) : (
              <img
                src={imgUrl}
                className={`max-h-[315px] w-full max-w-none object-cover object-center md:max-h-[450px] ${
                  isMedium ? 'rounded-[10px]' : ''
                }`}
                alt={`${productSKU.service.title} large logo`}
              />
            )}

            {url && (
              <ViewProductDetails url={url} className='hidden md:block' />
            )}
            {availability && (
              <ProductAvailability
                availability={availability}
                className='hidden md:block'
                displayFlag={displayFlag}
              />
            )}
            <VStack className='hidden w-full flex-col md:flex'>
              {redemptionInstructions && (
                <ProductRedemptionInstructions
                  redemptionInstructions={redemptionInstructions}
                />
              )}
              {termsAndConditions && (
                <ProductTermsAndConditions
                  termsAndConditions={termsAndConditions}
                />
              )}
            </VStack>
          </VStack>
          {/* ProductSKU availability + Purchase buttons */}
          <VStack className='w-full gap-3 p-4 pb-32 md:w-[45%] md:max-w-[500px] md:p-0'>
            <div className='flex flex-col gap-2'>
              <HStack className='w-full items-center justify-between'>
                <Text font='medium-md'>{productSKU.title}</Text>
              </HStack>
              <div className='flex flex-col gap-2 lg:flex-row lg:gap-4'>
                <HStack className='h-fit items-center gap-2'>
                  <ArcadeTicketIcon />
                  <Text font='md'>{productSKU.price}</Text>
                </HStack>
                {stock !== undefined && (
                  <Text className='text-nowrap'>
                    {account
                      ? stock > 0
                        ? `${productSKU.stock} in stock`
                        : 'Sold out'
                      : 'Connect to see availability'}
                  </Text>
                )}
              </div>
            </div>
            {url && <ViewProductDetails url={url} className='md:hidden' />}
            {availability && (
              <ProductAvailability
                availability={availability}
                className='md:hidden'
                displayFlag={displayFlag}
              />
            )}
            <VStack className='hidden pt-8 text-center md:flex md:gap-4'>
              {account && recordCount === 'JOIN_REQUIRED' && (
                <FeeView
                  coverageOverride={{ coverageType: 'all', covered: true }}
                />
              )}
              {account && functionId && programId && (
                <FeeView functionId={functionId} programId={programId} />
              )}
              {isPro &&
                isConnected &&
                inStock &&
                !['LOW', 'JOIN_REQUIRED'].includes(recordCount ?? '') && (
                  <AvailabilityCheckbox
                    checked={checked}
                    setChecked={setChecked}
                    availableInUserCountry={productSKU.availableInUserCountry}
                  />
                )}
              <PurchaseButton
                productSKU={productSKU}
                hasPendingPurchases={hasPendingPurchases}
                joinInfo={{
                  joinRecordCount,
                  isLoadingJoinParts,
                  recordsToJoin,
                  openJoinModal,
                  joinStatus,
                }}
                purchaseInfo={{
                  recordCount,
                  checked,
                  fee,
                  functionId,
                  partPurchaseRecords,
                  programId,
                }}
              />
              <Text font='sm'>Limit one store purchase per week</Text>
            </VStack>
            <VStack className='flex w-full md:hidden'>
              {redemptionInstructions && (
                <ProductRedemptionInstructions
                  redemptionInstructions={redemptionInstructions}
                />
              )}
              {termsAndConditions && (
                <ProductTermsAndConditions
                  termsAndConditions={termsAndConditions}
                />
              )}
            </VStack>
          </VStack>
        </div>
      </div>
      {isPuzzleWalletWebview === false && (
        <VStack className='absolute left-0 top-[72px] z-50 p-4 md:hidden'>
          <Button
            variant='icon'
            size='icon'
            onClick={() =>
              navigate({ to: index_route.to }).then(() => _close())
            }
          >
            <BiChevronLeft />
          </Button>
        </VStack>
      )}
      <VStack className='sticky bottom-0 z-10 w-full gap-4 bg-white p-4 text-center md:hidden'>
        {account && recordCount === 'JOIN_REQUIRED' && (
          <FeeView coverageOverride={{ coverageType: 'all', covered: true }} />
        )}
        {account && functionId && programId && (
          <FeeView functionId={functionId} programId={programId} />
        )}
        {isPro &&
          isConnected &&
          inStock &&
          !['LOW', 'JOIN_REQUIRED'].includes(recordCount ?? '') && (
            <AvailabilityCheckbox
              checked={checked}
              setChecked={setChecked}
              availableInUserCountry={productSKU.availableInUserCountry}
            />
          )}
        <PurchaseButton
          productSKU={productSKU}
          joinInfo={{
            joinRecordCount,
            isLoadingJoinParts,
            openJoinModal,
            recordsToJoin,
            joinStatus,
          }}
          purchaseInfo={{
            recordCount,
            checked,
            fee,
            functionId,
            partPurchaseRecords,
            programId,
          }}
        />
        <Text font='sm'>Limit one store purchase per week</Text>
      </VStack>
      <JoinStatusDialog
        eventStatuses={eventStatuses}
        joinStatus={joinStatus}
        joinRecordCount={joinRecordCount}
        open={joinDialogOpen}
        setOpen={setJoinDialogOpen}
        joinParts={joinParts}
      />
    </ProductPage>
  );
};
